import {
  Badge,
  Box,
  Center,
  Group,
  // Modal,
  Stack,
  Table,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
// import { IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
// import { useState } from "react";
import { type PlayerDetailFixtureResult } from "#app/routes/players+/player.$id.tsx";
import { useHints } from "#app/utils/client-hints.tsx";
import { getCurrentSquad } from "#app/utils/players.ts";
import { MATCH_FINISHED_STATUS } from "../../data-sync/constants";
import { type PlayerResult } from "../routes/latest";
import { MinutesPlayed } from "./badge-minutes-played";
import { CardTotal } from "./card-total";
import { AvatarProxy } from "./image-proxy";
import { MatchScore } from "./match-score";
import { PlayerRating } from "./player-rating";
// import { PlayerTableModal } from "./player-table-modal";
import { StatLabel } from "./stat-label";
import { TeamBadge } from "./team-badge";
import { WinLossBadge } from "./win-loss-badge";

type Props = {
  fixtures: Array<PlayerDetailFixtureResult>;
  player: PlayerResult;
};

export function PlayerFixturesList({ fixtures, player }: Props) {
  // const [modalPlayerFixture, setModalPlayerFixture] = useState<{
  //   player: PlayerResult;
  //   fixture: PlayerDetailFixtureResult;
  // } | null>(null);

  const { timeZone } = useHints();
  const theme = useMantineTheme();
  const isSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const squadTeamIds = player.SquadPlayer.map(
    (squadPlayer) => squadPlayer.teamId,
  );

  return (
    <Box>
      <Stack>
        <Table.ScrollContainer minWidth={720}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th miw={128}>Date</Table.Th>
                <Table.Th>Match</Table.Th>
                <Table.Th miw={{ base: 40, md: 180 }}>League</Table.Th>
                <Table.Th miw={120}>Result</Table.Th>
                <Table.Th>Rating</Table.Th>
                <Table.Th miw={140}>Minutes</Table.Th>
                <Table.Th>Stats</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fixtures.map((fixture) => {
                const fixtureDate = new Date(fixture.timestamp * 1000);

                const isHomeTeamPrimary = squadTeamIds.includes(
                  fixture.HomeTeam.id,
                );

                const squadPlayer = player.SquadPlayer.find((sp) => {
                  return [fixture.teamHomeId, fixture.teamAwayId].includes(
                    sp.teamId,
                  );
                });
                if (!squadPlayer) {
                  return null;
                }
                const { team } = squadPlayer;
                const isCurrentSquad =
                  getCurrentSquad(player)?.teamId === team.id;
                const playerStats = fixture.PlayerFixtureStatistics.find(
                  (stat) => stat.playerId === player.id,
                );

                // If no minutes - skip showing the match
                if (!playerStats?.minutes) {
                  return null;
                }

                return (
                  <Table.Tr
                    key={fixture.id}
                    style={{ cursor: "pointer" }}
                    // onClick={() => setModalPlayerFixture({ player, fixture })}
                  >
                    <Table.Td>
                      <Text size="sm" style={{ fontFamily: "monospace" }}>
                        {dayjs.tz(fixtureDate, timeZone).format("MMM D, YYYY")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group wrap="nowrap">
                        {!isCurrentSquad && (
                          <Box>
                            <Tooltip label={team.name}>
                              <Box>
                                <TeamBadge
                                  logo={
                                    isHomeTeamPrimary
                                      ? fixture.HomeTeam.logo
                                      : fixture.AwayTeam.logo
                                  }
                                />
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                        <Text>{isHomeTeamPrimary ? "vs" : "@"}</Text>
                        <TeamBadge
                          {...(!isHomeTeamPrimary
                            ? fixture.HomeTeam
                            : fixture.AwayTeam)}
                          pos="left"
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {isSm ? (
                        <Center>
                          <Tooltip label={fixture.League.name}>
                            <AvatarProxy
                              src={fixture.League.logo}
                              radius={0}
                              size="sm"
                              bg="white"
                            />
                          </Tooltip>
                        </Center>
                      ) : (
                        <Badge
                          color="gray"
                          variant="outline"
                          radius="xs"
                          size="sm"
                        >
                          {fixture.League.name}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group wrap="nowrap">
                        {MATCH_FINISHED_STATUS.includes(
                          fixture.status_short || "",
                        ) ? (
                          <WinLossBadge teamId={team.id} fixture={fixture} />
                        ) : fixture.status_short !== "NS" ? (
                          <Badge variant="dot" color="green">
                            {fixture.status_elapsed}'
                          </Badge>
                        ) : null}
                        {fixture.status_short !== "NS" && (
                          <MatchScore
                            homeScore={fixture.score_home}
                            awayScore={fixture.score_away}
                            primaryTeamLocation={
                              isHomeTeamPrimary ? "home" : "away"
                            }
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <PlayerRating rating={playerStats?.rating} />
                    </Table.Td>
                    <Table.Td>
                      <Group wrap="nowrap" gap={6}>
                        <MinutesPlayed
                          minutes={playerStats?.minutes}
                          substitute={playerStats?.substitute}
                          size="sm"
                        />
                        <Group gap={0} wrap="nowrap">
                          <CardTotal
                            type="yellow"
                            count={playerStats?.cards_yellow}
                            withTooltip
                          />
                          <CardTotal
                            type="red"
                            count={playerStats?.cards_red}
                            withTooltip
                          />
                        </Group>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {playerStats?.goals_total ? (
                          <StatLabel
                            label="goal"
                            emoji="⚽️"
                            value={playerStats.goals_total}
                          />
                        ) : null}
                        {playerStats?.goals_assists ? (
                          <StatLabel
                            label="assist"
                            emoji="👟"
                            value={playerStats.goals_assists}
                          />
                        ) : null}
                        {playerStats?.goals_saves ? (
                          <StatLabel
                            label="save"
                            emoji="🧤"
                            value={playerStats.goals_saves}
                          />
                        ) : null}
                        {playerStats?.goals_conceded ? (
                          <StatLabel
                            label="goal"
                            emoji="❌"
                            value={playerStats.goals_conceded}
                            extraLabel="conceded"
                          />
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>

      {/* <Modal.Root
        opened={!!modalPlayerFixture}
        onClose={() => setModalPlayerFixture(null)}
        transitionProps={{ duration: 150 }}
        size="lg"
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header style={{ height: 0, minHeight: 0 }} p={0} mb="sm">
            <Modal.CloseButton
              style={{ position: "absolute", top: 0, right: 0 }}
              icon={<IconX color="white" />}
              size="xl"
              bg="dark"
              radius={0}
            />
          </Modal.Header>
          <Modal.Body>
            {modalPlayerFixture && (
              <PlayerTableModal
                player={modalPlayerFixture.player}
                fixture={modalPlayerFixture.fixture}
              />
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Root> */}
    </Box>
  );
}
